import { QUESTIONS, Question } from "../../constants";
import { Button } from "../shared/Button";
import { ProgressBar } from "../shared/ProgressBar";
import { SurveyIntro } from "./SurveyIntro";
import classNames from "classnames";
import Caret from '../../assets/images/caret.svg';
import GreenBlob from '../../assets/images/mobile/green-blob.png';
import PurpleBlob from '../../assets/images/mobile/purple-blob.png';
import BlueBlob from '../../assets/images/mobile/blue-blob.png';
import './Survey.scss';
import { AppView } from "../Main/Main";
import { useEffect, useState } from "react";
import { determineEngageType, determinePerceiveType, determineThinkType } from "../Results/utils";

export enum SurveyView {
  Intro = 'intro',
  Main = 'main'
}

function Answer({ text, choiceText, onClick, selected }: { text: string, choiceText: string, onClick: () => void, selected: boolean }) {
  return (
    <button
      role="menuitem"
      onClick={onClick}
      className={classNames(
        selected ? "selected border-2 md:border-[3px] border-crayola-pink bg-crayola-pale-pink" : "bg-white border-2 border-[#56575F] md:border md:hover:border-2 md:hover:border-crayola-pink",
        "group answer flex rounded-2xl text-xl flex-col justify-start md:justify-center gap-0 items-center cursor-pointer h-8 w-8 relative bottom-4 md:bottom-0 md:h-[95px] md:w-[184px]"
      )}
    >
      {selected && <div className="w-5 h-5 absolute top-1 rounded-full bg-crayola-pink md:hidden"></div>}
      <span
        className={classNames(
          selected ? "text-crayola-dark-blue" : "text-[#56575F]",
          "underline-animate w-max max-w-[70px] md:w-auto md:max-w-full relative top-9 md:top-0 text-xs md:text-base"
        )}
      >
        {choiceText}
      </span>
      <span
        className={classNames(
          selected ? "text-crayola-dark-blue" : "text-crayola-blue",
          "italic text-sm relative top-9 md:top-[2px] md:text-base lg:text-xl font-semibold"
        )}
      >{text}</span>
      <span
        className={classNames(
          selected ? "text-crayola-dark-blue" : "text-crayola-blue",
          "italic text-sm relative w-[200%] md:w-auto top-9 md:top-0 md:text-base font-medium md:font-semibold"
        )}
      >
        Like Me
      </span>
    </button>
  );
}
  
function QuestionSelect({
  question,
  questionNumber,
  answers,
  onAnswerSelect
}: {
  question: Question;
  questionNumber: number;
  answers: { [qNumber: number]: number; };
  onAnswerSelect: (questionNumber: number, answer: number) => void;
}) {
  
  return (
    <div
      role="menu"
      aria-label={`${question.optionOne} or ${question.optionTwo}`}
      className="bg-white w-full h-[175px] md:h-[190px] flex items-center md:justify-between flex-col gap-3 md:rounded-3xl py-3.5 shadow-[0_2px_4px_0_rgba(0,0,0,0.1)] md:shadow-[0_0_12px_0_rgba(0,0,0,0.15)]"
    >
      <div className="choices flex justify-between w-11/12 text-crayola-blue text-lg md:text-3xl font-semibold">
        <span className="choice">{ question.optionOne }</span>
        <span className="choice">{ question.optionTwo }</span>
      </div>
      <div className="relative flex justify-between w-11/12 pt-2">
        <div className="flex absolute top-2 md:-top-1 justify-between w-full border-t border-[#AAAAB4] border-dashed">
          <img alt="" src={Caret} className="caret relative right-[15px]"/>
          <img alt="" src={Caret} className="caret relative left-[15px] rotate-180"/>
        </div>
        <div className="hidden lg:flex w-[48%] gap-1/2 md:gap-4">
          <Answer text="Definitely" choiceText={question.optionOne} selected={answers[questionNumber] === -2} onClick={() => onAnswerSelect(questionNumber, -2 )} />
          <Answer text="Somewhat" choiceText={question.optionOne} selected={answers[questionNumber] === -1} onClick={() => onAnswerSelect(questionNumber, -1 )} />
        </div>
        <div className="hidden lg:flex w-[48%] gap-1/2 md:gap-4">
          <Answer text="Somewhat" choiceText={question.optionTwo} selected={answers[questionNumber] === 1} onClick={() => onAnswerSelect(questionNumber, 1 )} />
          <Answer text="Definitely" choiceText={question.optionTwo} selected={answers[questionNumber] === 2} onClick={() => onAnswerSelect(questionNumber, 2 )} />
        </div>
        <div className="flex justify-center w-full lg:hidden">
          <div className="flex w-5/6 md:w-full justify-between">
            <Answer text="Definitely" choiceText={question.optionOne} selected={answers[questionNumber] === -2} onClick={() => onAnswerSelect(questionNumber, -2 )} />
            <Answer text="Somewhat" choiceText={question.optionOne} selected={answers[questionNumber] === -1} onClick={() => onAnswerSelect(questionNumber, -1 )} />
            <Answer text="Somewhat" choiceText={question.optionTwo} selected={answers[questionNumber] === 1} onClick={() => onAnswerSelect(questionNumber, 1 )} />
            <Answer text="Definitely" choiceText={question.optionTwo} selected={answers[questionNumber] === 2} onClick={() => onAnswerSelect(questionNumber, 2 )} />
          </div>
        </div>
      </div>
    </div>
  )
}
  
function QuestionBlock({ 
  questions,
  pageNumber,
  answers,
  onAnswerSelect
}: { 
  questions: Question[];
  pageNumber: number;
  answers: { [qNumber: number]: number };
  onAnswerSelect: (questionNumber: number, answer: number) => void;
}) {
  return (
    <div key={`animation-${pageNumber}`} className="animate-in duration-700 fade-in slide-in-from-bottom-8 flex flex-col gap-2 md:gap-4 items-center w-full">
      { 
        questions.map((question, i) => (
          <QuestionSelect
            key={`question-${i}`}
            question={question}
            questionNumber={i + (4 * pageNumber)}
            answers={answers}
            onAnswerSelect={onAnswerSelect}
          />
        ))
      }
    </div>
  );
}

export function Survey({
  answers,
  setView,
  setSurveyView,
  surveyView,
  onAnswerSelect,
  pageNumber,
  setPageNumber
}: {
  answers: { [qNumber: number]: number };
  setView: (view: AppView) => void;
  setSurveyView: (view: SurveyView) => void;
  surveyView: SurveyView;
  onAnswerSelect: (questionNumber: number, answer: number) => void;
  pageNumber: number;
  setPageNumber: (page: number) => void;
}) {
  const [buttonDisabled, setButtonDisabled] = useState(false);
  useEffect(() => { window.scrollTo(0, 0) }, [pageNumber, surveyView]);
  const currentQuestions = QUESTIONS.slice((pageNumber * 4), (pageNumber * 4 + 4));

  function sendResults() {
    const thinkType = determineThinkType(answers);
    const engageType = determineEngageType(answers);
    const perceiveType = determinePerceiveType(answers);
    setButtonDisabled(true);
    
    fetch(process.env.REACT_APP_FUNCTION_URL as string, {
      method: 'POST',
      body: JSON.stringify({ thinkType, engageType, perceiveType })
    })
      .catch(e => console.log(e))
      .finally(() => setView(AppView.Form))
  }
  
  return (
    <div id="main-content" className="w-full h-full relative overflow-hidden">
      <h1 className="absolute left-[-9999px]">The Crayola Creative Style Tool</h1>
      <div className="w-full flex flex-col items-center relative z-30">
        <ProgressBar
          isIntro={surveyView === SurveyView.Intro}
          pageNumber={pageNumber}
          onRestart={() => {
            setSurveyView(SurveyView.Intro);
            setPageNumber(0);
          }}
          onProgressClick={(pageNumber: number) => setPageNumber(pageNumber)} 
        />
        {surveyView === SurveyView.Intro &&
          <SurveyIntro onNextClick={() => setSurveyView(SurveyView.Main)} />
        }
        {surveyView === SurveyView.Main && <main role="main" className="w-full flex-col items-center md:mt-6 lg:flex-row lg:items-start justify-center flex gap-12 lg:gap-0 min-[1512px]:gap-[92px]">
          <div className="left-column px-4 md:px-0 w-full md:w-[688px] lg:w-[500px] items-center lg:items-start flex flex-col gap-5">
            <h1 className="font-semibold italic text-2xl md:text-center md:text-3xl lg:text-5xl lg:text-left text-crayola-blue">Select the degree to which the word or phrase describes you.</h1>
            { pageNumber === 0 && <p className="text-xl w-full lg:w-[450px]">For each pair, select one word or phrase that describes you best, indicating the extent to which that word or phrase captures you (i.e., if you describe yourself more by the word on the left, choose definitely like me or somewhat like me on the left but if the word on the right describes you better, choose definitely like me or somewhat like me on the right). There are no right or wrong answers; this is merely your opinion as to which word or phrase is more descriptive of you or the way you think.</p> }
          </div>
          <div className="right-column w-full md:w-[820px] lg:w-[900px] h-full">
            <QuestionBlock
              answers={answers}
              onAnswerSelect={onAnswerSelect}
              pageNumber={pageNumber}
              questions={currentQuestions}
            />
            <div className="button-row flex justify-center gap-8 mt-16">
              <Button
                rightArrow
                type="outline"
                onClick={() => {
                  if (pageNumber === 0) {
                    setSurveyView(SurveyView.Intro);
                  } else {
                    setPageNumber(pageNumber - 1);
                  }
                }}
                text="BACK"
              />
              <Button
                type="solid"
                onClick={() => {
                  if (pageNumber === 8) {
                    sendResults();
                  } else {
                    setPageNumber(pageNumber + 1);
                  }
                }}
                rightArrow
                text="NEXT"
                disabled={!currentQuestions.every(q => answers[q.number]) || buttonDisabled}
              />
            </div>
          </div>
          <div className="md:hidden w-full">
            {pageNumber >= 0 && pageNumber < 3 && <img alt="" src={GreenBlob} className="w-full" />}
            {pageNumber >= 3 && pageNumber < 6 && <img alt="" src={PurpleBlob} className="w-full" />}
            {pageNumber >= 6 && <img alt="" src={BlueBlob} className="w-full" />}
          </div>
        </main>}
      </div>
    </div>
  );
}